import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLButtonElement} [showMoreToggle] - The show more/less toggle button.
 * @property {HTMLElement} [showMoreText] - The show more text element.
 * @property {HTMLElement} [showLessText] - The show less text element.
 * @property {HTMLElement} [hiddenItemsWrapper] - The hidden items wrapper element.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Set up show more/less toggle if it exists
    if (this.refs.showMoreToggle && this.refs.hiddenItemsWrapper) {
      this.refs.showMoreToggle.addEventListener('click', this.#handleShowMoreToggle, { signal });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles the show more/less toggle button click.
   * Uses actual content height for smooth accordion animation in both directions.
   */
  #handleShowMoreToggle = () => {
    const wrapper = this.refs.hiddenItemsWrapper;
    if (!wrapper) return;

    const isExpanded = this.refs.showMoreToggle?.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      // Collapse: Get height while still expanded, then animate to 0
      const currentHeight = wrapper.scrollHeight;
      wrapper.removeAttribute('data-expanded');
      wrapper.style.maxHeight = `${currentHeight}px`;

      // Force reflow to ensure the browser registers the explicit height
      wrapper.offsetHeight;

      // Now animate to 0
      wrapper.style.maxHeight = '0px';
      wrapper.inert = true;
      this.refs.showMoreToggle?.setAttribute('aria-expanded', 'false');
      this.refs.showMoreText?.classList.remove('hidden');
      this.refs.showLessText?.classList.add('hidden');
    } else {
      // Expand: Animate to actual content height
      const targetHeight = wrapper.scrollHeight;
      wrapper.style.maxHeight = `${targetHeight}px`;
      wrapper.inert = false;
      this.refs.showMoreToggle?.setAttribute('aria-expanded', 'true');
      this.refs.showMoreText?.classList.add('hidden');
      this.refs.showLessText?.classList.remove('hidden');

      // After animation completes, remove inline style to allow natural resizing
      wrapper.addEventListener(
        'transitionend',
        () => {
          if (this.refs.showMoreToggle?.getAttribute('aria-expanded') === 'true') {
            wrapper.style.maxHeight = '';
            wrapper.setAttribute('data-expanded', '');
          }
        },
        { once: true }
      );
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
